import React from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';

import {Icon, Drawer} from '@stubhub/bamboo';
import {Keys} from '@stubhub/javascript-event-codes';

import '../styles/index.scss';

const DEFAULT_WINDOW_WIDTH = 600;
const SMALL_SCREEN_WIDTH = 640;
const OPTION_HEIGHT = 46;

const {esc, down, enter, up} = Keys;

class DropdownList extends React.Component {
  constructor(props) {
    super(props);
    const {selected, highlighted} = this.props;
    this.state = {
      active: false,
      selected: this.validateItem(selected),
      highlighted,
    };
    const windowWidth = __CLIENT__ ? window.innerWidth : DEFAULT_WINDOW_WIDTH;
    this.isSmall = windowWidth < SMALL_SCREEN_WIDTH;
  }

  /**
   *  Method the Validate the Current item is present in the current List of the items.
   *  @param item [object] The Item object Instance.
   *
   */
  validateItem(item) {
    if (!item) {
      return null;
    }

    let key;
    const {key: itemKey} = item;
    if (item.key) {
      key = itemKey;
    } else if (typeof item === 'string') {
      key = item;
    }

    return this.getItemByKey(key).item || item;
  }

  /**
   * Method to get the Item from the Items Prop using the index of the item.
   * @param index [number] The param containing the index value to used to find the item.
   *
   */
  getItemByIndex = (index) => {
    const {items} = this.props;

    return items[index];
  };

  /**
   * Method to Find the Item from the Items Prop using the Key of the item.
   * @param key [string/number] The param containing the key value to used to find the item.
   *
   */
  getItemByKey = (key) => {
    let item;
    let itemIndex;
    const {items} = this.props;
    items.some((itemObj, index) => {
      if (itemObj.key === key) {
        item = itemObj;
        itemIndex = index;

        return true;
      }

      return false;
    });

    return {
      itemIndex,
      item,
    };
  };

  /**
   * Method to get the next Selectable Item from the list of items based on the direction.
   * @param direction [string] The String representing the direction of the keys pressed.
   *
   */
  getNextSelectableItem = (direction = up) => {
    const {active} = this.state;
    if (active) {
      const {highlighted} = this.state;
      let index = 0;

      /**
       * If an Option is already Highlighted use that as the start.
       *
       */
      if (highlighted) {
        /**
         * Get the Index of the Current Item
         *
         */
        const currentItem = this.getItemByKey(highlighted);
        const currentItemIndex = currentItem.itemIndex;

        if (currentItemIndex >= 0) {
          if (direction === up) {
            index = currentItemIndex - 1;
          } else {
            index = currentItemIndex + 1;
          }

          const {items} = this.props;
          const itemLen = items.length;

          if (index >= itemLen) {
            index = 0;
          } else if (index < 0) {
            index = itemLen - 1;
          }
        }
      }

      return this.getItemByIndex(index);
    }
  };

  /**
   * Method to handle document click which hides items when click outside current dropdownlist.
   * @param e [object] The Synthetic React event object generated for the 'onKeydown' event listener.
   */
  handleDocumentClick = (e) => {
    const {active} = this.state;
    if (this.state && active && this.rootNode && !this.rootNode.contains(e.target)) {
      this.focusOut(e);
    }
  };

  /**
   * Wrapper Method to Handle the `up` or 'down' key event.
   * @param event [object] The Synthetic React event object generated for the 'onKeydown' event listener.
   * @param direction [string] The String representing the direction of the keys pressed.
   *
   */
  handleArrowKey = (event, direction) => {
    if (event) {
      event.preventDefault();
    }

    /**
     * Get the item to be higlighted based on the direction
     *
     */
    const item = direction ? this.getNextSelectableItem(direction) : null;
    const itemKey = item && item.key;
    if (itemKey) {
      this.highlightItem(itemKey);
    }
  };

  /**
   * Wrapper Method to Handle the `esc` key event.
   * @param event [object] The Synthetic React event object generated for the 'onKeydown' event listener.
   *
   */
  handleEscKey = (event) => {
    if (event) {
      event.preventDefault();
    }
    this.close(event);
  };

  /**
   * Wrapper Method to Handle the `enter` key event.
   * @param event [object] The Synthetic React event object generated for the 'onKeydown' event listener.
   *
   */
  handleEnterKey = (event) => {
    if (event) {
      event.preventDefault();
    }
    const {highlighted} = this.state;

    if (highlighted) {
      const {item} = this.getItemByKey(highlighted);
      if (item) {
        this.onSelect(item, event);
      }
    }
  };

  /**
   * Method to format the display name for the current Item.
   *  @param item [object] The Item object Instance.
   *
   */
  formatSelectionDisplay = (item) => {
    // If param is key, find the item
    item = this.validateItem(item);

    if (item) {
      const {parseSelectionDisplay, start, end} = this.props;
      if (parseSelectionDisplay) {
        return parseSelectionDisplay({
          key: item.key,
          name: item.name || item,
          start,
          end,
        });
      }

      return item.name;
    }

    return item ? item.name : null;
  };

  /**
   * Method to handle the selection of an item option.
   * @param item [object] The Item object Instance.
   * @param event [object] The Synthetic React event object generated for the 'onKeydown' event listener.
   *
   */
  onSelect = (item, event) => {
    const {didMove, selected: selectedState} = this.state;
    const {onSelection, onClick} = this.props;
    if (didMove) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      this.setState({didMove: false});
    } else {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      const prevSelected = selectedState && selectedState.key ? selectedState.key : null;
      const selected = item.key || null;
      if (selected === 'choose-date') {
        onSelection(item);
        this.setState({active: false, selected: item, highlighted: null});
      } else {
        let defer;
        if (prevSelected !== selected) {
          // Invoke callback if value is different than previous.
          defer = onSelection(item);
        } else {
          onClick(event);
        }
        if (defer && defer.then) {
          if (selected === '$0') {
            this.setState({active: false, selected: item, highlighted: null});
          }
          defer
            .then((deferResult) => {
              this.setState({active: false, selected: deferResult || item, highlighted: null});

              return '';
            })
            .catch(() => {
              this.setState({active: false});
            });
        } else {
          this.setState({active: false, selected: item, highlighted: null});
        }
      }
    }
  };

  /**
   * Handler Method for the 'onKeyDownHandler' event listener.
   * @param event [object] The Synthetic React event object generated for the 'onKeydown' event listener.
   *
   */
  onKeyDownHandler = (event) => {
    const {keyCode} = event || {};

    switch (keyCode) {
      case up:
      case down:
        this.handleArrowKey(event, keyCode);
        break;

      case esc:
        this.handleEscKey(event);
        break;

      case enter: {
        this.handleEnterKey(event);
        break;
      }
    }
  };

  /**
   * Method to handle the Dropdown Expand Action to contents of the dropdown.
   *
   * @fires props.onClick
   * @fires props.onExpand
   *
   */
  expand = (e) => {
    const {enabled} = this.props;
    this.setState({active: enabled, highlighted: null});

    /**
     * Attach the Keydown Handler to the Dropdown Component
     *
     */
    const {rootNode} = this;
    const {onClick, onExpand} = this.props;
    rootNode && rootNode.addEventListener('keydown', this.onKeyDownHandler);
    onClick(e);
    onExpand(e);
  };

  /**
   * Close the dropdownlist
   * @fires props.onClick
   * @fires props.onClose
   */
  close = (e) => {
    this._close(e);

    const {onClick, onClose} = this.props;
    onClick(e);
    onClose(e);
  };

  /**
   * The callback function when draggable close
   *
   * Will not trigger props.onClose when user select any option as onSelection will be triggered in that case
   *
   */
  handleDraggableClose = (e) => {
    const {onClose} = this.props;
    const {selectionOptions, dropDownOptions} = this;
    if (
      selectionOptions &&
      !selectionOptions.contains(e.target) &&
      dropDownOptions &&
      dropDownOptions.contains(e.target)
    ) {
      onClose(e);
    }
  };

  /**
   * Close the dropdownlist and trigger onClose callback
   *
   * @fires props.onClose
   */
  focusOut = (e) => {
    const {onClose} = this.props;
    const focusOutEvent = {...e, focusout: true};
    this._close();
    onClose(focusOutEvent);
  };

  /**
   * Method to handle the Dropdown Close Action and reset the state of the dropdown.
   *
   * Fire no events
   *
   */
  _close = () => {
    this.setState({active: false, highlighted: null, didMove: false});

    /**
     * Remove the Keydown Handler from the Dropdown Component
     *
     */
    const {rootNode} = this;
    rootNode && rootNode.removeEventListener('keydown', this.onKeyDownHandler);
  };

  /**
   * Method to handle the Dropdown Expand and Close Action as a toggle state
   *
   */
  toggleMenu = (e) => {
    const {active} = this.state;
    active ? this.close(e) : this.expand(e);
  };

  /**
   * Method to Highlight the Item with the selected key
   * @param key [string/number] The param containing the key value for the key of the item to highlight.
   *
   */
  highlightItem = (key) => {
    this.setState({highlighted: key});
  };

  /**
   * Method to remove the Highlight the Item with the selected key
   *
   */
  clearHighlightItem = () => {
    this.setState({highlighted: null});
  };

  /**
   * React Component Lifecycle Methods.
   *
   */
  componentDidMount() {
    document.addEventListener('click', this.handleDocumentClick);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const {selected} = newProps;
    this.setState((state) => {
      const currentSelected = state.selected || {};
      const newState = {};

      /*
       * If the component Received the Updated Selected Value, then update the state to match the selected item.
       * Added check for EVNTBLLT-54 : Clicking 'See all tickets' or reset will not reset the dropdown list.
       *
       */
      if (selected && currentSelected.key !== selected.key) {
        newState.selected = selected;
      }
      if (!state.active && newProps.active) {
        newState.active = true;
      }

      return newState;
    });
  }

  renderNativeSelect() {
    const {selected} = this.state;
    const {items} = this.props;
    const options = items.map((item) => {
      return (
        <option
          disabled={item.disabled}
          value={item.key} // Need all the data when calling onSelect in select.onChange handler.
          key={item.key}
        >
          {item.name}
        </option>
      );
    });

    return (
      selected && (
        <select onChange={(e) => this.onSelect({key: e.currentTarget.value}, e)} value={selected.key}>
          {options}
        </select>
      )
    );
  }

  move = () => {
    this.setState({didMove: true});
  };

  // Don't use draggable if viewport is not small
  useDraggable = (draggable) => {
    return draggable && this.isSmall;
  };

  renderCustomSelect() {
    const {
      items,
      selectionTitle,
      showDraggableTitle,
      hideOptionsBorder,
      scrollable,
      scrollOnItems,
      highlightClass,
      highlightBold,
      useOldVersion,
      useCheckMark,
      draggable: draggableProp,
    } = this.props;
    const draggable = this.useDraggable(draggableProp);

    const {highlighted, selected, active} = this.state;
    // For Merch header implementation only
    const geoLocateIcon = <Icon name="GeoLocation" className="DropdownList__item--geoLocateIcon" />;
    if (active) {
      const selections = (
        <ul className="DropdownList__items" onMouseOut={this.clearHighlightItem}>
          {items.map((item) => {
            const style = {};
            const itemClasses = ['DropdownList__item'];
            let showOk = false;
            let showGeoIcon = false;
            const ok = <i className="sh-iconset sh-iconset-ok" style={{fontSize: '4px'}} />;
            let checkMark = null;

            if (!useOldVersion) {
              if (highlighted === item.key || (!highlighted && selected && item.key === selected.key)) {
                if (!useCheckMark) {
                  itemClasses.push('DropdownList__item--highlighted');
                }
                if (highlightClass) {
                  itemClasses.push(highlightClass);
                }
              }
              if (useCheckMark && selected && item.key === selected.key) {
                checkMark = <Icon className="DropdownList__item__check" name="Check" width="12" height="12" />;
              }
              if (selected && highlightBold && item.key === selected.key) {
                style.fontWeight = '900';
                style.fontSize = '18px';
              }
              if (useCheckMark) {
                style.display = 'flex';
              }
              if (item.disabled) {
                itemClasses.push('DropdownList__item--disabled');
              }
              if (selected && item.key === selected.key) {
                itemClasses.push('DropdownList__item--selected');
              }
            } else if (selected && item.key === selected.key) {
              showOk = true;
            }

            if (item.key === '$0') {
              showGeoIcon = true;
            }

            return (
              <li
                style={style}
                key={item.key}
                className={cs(itemClasses)}
                onMouseEnter={this.highlightItem.bind(this, item.key)}
                onClick={(e) => !item.disabled && this.onSelect(item, e)}
              >
                <div className="DropdownList__item__name">{item.name}</div>
                {showGeoIcon && geoLocateIcon}
                {showOk && ok}
                <div className="DropdownList__item__checkMark">{checkMark}</div>
              </li>
            );
          })}
        </ul>
      );

      const optionsClasses = cs({
        DropdownList__options: true,
        'DropdownList__options--no-border': hideOptionsBorder,
        Draggable: draggable,
      });
      const titleClasses = cs({
        DropdownList__title: true,
        'DropdownList__title--show': showDraggableTitle,
      });
      const optionsStyle = {};
      if (scrollable) {
        optionsStyle.overflow = 'auto';
      }

      if (scrollOnItems && items.length > scrollOnItems) {
        optionsStyle.height = scrollOnItems * OPTION_HEIGHT;
      }
      const dropdownList = (
        <div className={optionsClasses} style={optionsStyle} ref={(node) => (this.dropDownOptions = node)}>
          {selectionTitle && (!draggable || showDraggableTitle) ? (
            <div className={titleClasses}>{selectionTitle}</div>
          ) : null}
          <div className="select-options-scrollable" ref={(node) => (this.selectionOptions = node)}>
            {selections}
          </div>
        </div>
      );

      return (
        <div className={cs('DropdownList__DPC1', {Draggable: draggable})}>
          <div className="DropdownList__DPC2">
            {draggable ? (
              <Drawer
                className="DropdownList__Drawer"
                onDragDrawer={this.move}
                onReleaseDrawer={this._close}
                onToggleDrawer={this.handleDraggableClose}
                defaultOpen
              >
                {dropdownList}
              </Drawer>
            ) : (
              <div className="draggablePlaceHolder">{dropdownList}</div>
            )}
          </div>
        </div>
      );
    }

    return null;
  }

  renderSelectionPanel() {
    const {useNative} = this.props;

    return useNative ? this.renderNativeSelect() : this.renderCustomSelect();
  }

  render() {
    const {active, selected} = this.state;
    // Here displayClassName is style applied form event-sorter.js for search and browse page dropdown
    const {
      className,
      iconCls,
      icon,
      iconColor,
      closeable = true,
      displayClassName,
      height,
      dropDownToggle = false,
      dropdownAlign,
      draggable: draggableProp,
    } = this.props;
    const draggable = this.useDraggable(draggableProp);

    const onClickDropDown = dropDownToggle ? this.toggleMenu : this.expand;

    const closeIcon = closeable && active && (
      <div className="DropdownList__close" onClick={this.close}>
        {draggable ? null : <span className="sh-iconset sh-iconset-close DropdownList__close-icon" />}
      </div>
    );

    const style = {};
    if (height) {
      style.height = height;
    }

    const classNames = cs(
      ['DropdownList', className],
      {Draggable: draggable},
      {'DropdownList--right': !this.isSmall && dropdownAlign === 'right'}
    );

    const displayIconClassNames = cs(['sh-iconset', 'DropdownList__display-icon', iconCls]);

    const buttonIcon = icon ? icon(iconColor ? {fill: iconColor} : {}) : null;
    const dropdownButtonContents = (
      <div className={cs('DropdownList__display', displayClassName)}>
        {iconCls ? <div className={displayIconClassNames}>{buttonIcon}</div> : null}
        <div className="DropdownList__display-text">{this.formatSelectionDisplay(selected)}</div>
        <span className={cs('sh-iconset', 'sh-iconset-chevron', `${active ? 'up-arrow' : 'down-arrow'}`)} />
      </div>
    );

    // A link that manages the opening of the dropdown modal
    const dropdownWithLink = (
      <a onClick={onClickDropDown} className="DropdownList__wrapper">
        {dropdownButtonContents}
      </a>
    );

    return (
      <div className={classNames} ref={(node) => (this.rootNode = node)} style={{...style}}>
        {dropdownWithLink}
        {this.renderSelectionPanel()}
        {closeIcon}
      </div>
    );
  }
}

DropdownList.propTypes = {
  items: PropTypes.array,
  onSelection: PropTypes.func,
  onClick: PropTypes.func,
  onClose: PropTypes.func,
  onExpand: PropTypes.func,
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  highlighted: PropTypes.array,
  highlightClass: PropTypes.string,
  selectionTitle: PropTypes.node,
  showDraggableTitle: PropTypes.bool,
  closeable: PropTypes.bool,
  hideOptionsBorder: PropTypes.bool,
  useOldVersion: PropTypes.bool,
  useCheckMark: PropTypes.bool,
  draggable: PropTypes.bool,
  useNative: PropTypes.bool,
  className: PropTypes.string,
  icon: PropTypes.func,
  dropDownToggle: PropTypes.bool,
  dropdownAlign: PropTypes.oneOf(['left', 'right']),
  parseSelectionDisplay: PropTypes.func,
  start: PropTypes.string,
  end: PropTypes.string,
  scrollable: PropTypes.bool,
  scrollOnItems: PropTypes.number,
  highlightBold: PropTypes.bool,
  iconCls: PropTypes.string,
  iconColor: PropTypes.string,
  displayClassName: PropTypes.string,
  height: PropTypes.number,
  enabled: PropTypes.bool,
};

DropdownList.defaultProps = {
  items: [],
  useOldVersion: false,
  useCheckMark: false,
  icon: null,
  draggable: false,
  useNative: false,
  onSelection: () => undefined,
  onClick: () => undefined,
  onExpand: () => undefined,
  onClose: () => undefined,
  dropdownAlign: 'right',
  enabled: true,
};

export default DropdownList;
